import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import { useDatabase } from '../../context/DatabaseContext';
import { doc, updateDoc, addDoc, deleteDoc, writeBatch, collection } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ITEMS_PER_PAGE = 10;
const EMPTY = {
  name: '',
  nameSindhi: '',
  districtId: '',
  cityVillage: '',
  cityVillageSindhi: '',
  established: '',
  status: 'active',
  description: '',
  descriptionSindhi: '',
};

export default function ManageBranches() {
  const t = useT();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRTL = t.language === 'sindhi';
  const { getCollection, db } = useDatabase();

  const filterDistrictId = searchParams.get('districtId');
  const isReadOnly = searchParams.get('readOnly') === 'true';

  const role = user?.role || '';
  const isMain = role === 'main_admin';
  const isCenterBody = role?.startsWith('center_body_');
  const isDistCoord = role === 'district_coordinator';
  const isGlobalAdmin = isMain || isCenterBody;

  const [branches, setBranches] = useState([]);
  const [unactiveBranches, setUnactiveBranches] = useState([]);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [tab, setTab] = useState('active');
  const [modal, setModal] = useState(false);
  const [secretaryModal, setSecretaryModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const allBranches = await getCollection('branches');
      let filteredBranches = allBranches;

      const effectiveDistrictId = filterDistrictId || (isDistCoord ? user?.districtId : null);

      if (effectiveDistrictId) {
        filteredBranches = allBranches.filter(b => b.districtId === effectiveDistrictId);
      }

      setBranches(filteredBranches.filter(b => b.status === 'active'));
      setUnactiveBranches(filteredBranches.filter(b => b.status === 'unactive'));

      const allDistricts = await getCollection('districts');
      setDistricts(allDistricts);

      const allMembers = await getCollection('members');
      setApprovedMembers(allMembers.filter(m => m.status === 'approved'));

    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openAdd = () => {
    setForm({ ...EMPTY, districtId: isDistCoord ? (user?.districtId || '') : '' });
    setEditId(null);
    setModal(true);
  };

  const openEdit = b => {
    let establishedDate = '';
    if (b.established) {
      if (b.established.toDate) {
        establishedDate = b.established.toDate().toISOString().split('T')[0];
      } else if (typeof b.established === 'string') {
        establishedDate = b.established.split('T')[0];
      }
    }
    setForm({ ...b, established: establishedDate });
    setEditId(b._id);
    setModal(true);
  };

  const save = async e => {
    e.preventDefault();
    try {
      if (editId) {
        await updateDoc(doc(db, 'branches', editId), form);
        toast.success(t('branchUpdated'));
      } else {
        await addDoc(collection(db, 'branches'), { ...form, status: 'active', createdAt: new Date().toISOString() });
        toast.success(t('branchCreated'));
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error('Error saving branch');
    }
  };

  const approve = async (id, status) => {
    try {
      await updateDoc(doc(db, 'branches', id), { status });
      toast.success(status === 'active' ? t('approvedMsg') : t('rejectedMsg'));
      load();
    } catch { toast.error('Error'); }
  };

  const deleteBranch = async id => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'branches', id));
      toast.success(t('deletedMsg'));
      load();
    } catch { toast.error('Error'); }
  };

  const assignSecretary = async (member) => {
    if (!member.userId) {
      toast.error("This member does not have a linked user account.");
      return;
    }
    try {
      const batch = writeBatch(db);

      const branchRef = doc(db, 'branches', selectedBranch._id);
      batch.update(branchRef, { secretaryId: member._id });

      const userRef = doc(db, 'users', member.userId);
      batch.update(userRef, { role: 'branch_secretary_general', branchId: selectedBranch._id });

      await batch.commit();

      toast.success(t('branchSecretaryUpdated'));
      setSecretaryModal(false);
      load();
    } catch (err) {
      console.error("Error assigning secretary:", err);
      toast.error('Error assigning secretary.');
    }
  };

  const list = tab === 'active' ? branches : unactiveBranches;
  const renderDistName = (d) => isRTL ? (d.nameSindhi || d.name) : d.name;
  const renderBranchName = (b) => isRTL ? (b.nameSindhi || b.name) : b.name;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {t('manageBranches')} {filterDistrictId && districts.find(d => d._id === filterDistrictId) && `(${isRTL ? districts.find(d => d._id === filterDistrictId).nameSindhi : districts.find(d => d._id === filterDistrictId).name})`}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {branches.length} {t('activeBranches')} · {unactiveBranches.length} {t('unactive')}
            {filterDistrictId && <button onClick={() => setSearchParams({})} style={{ background: 'none', border: 'none', color: 'var(--sindhi-maroon)', cursor: 'pointer', marginLeft: 12, fontWeight: 700, textDecoration: 'underline' }}>{t('clearFilter')}</button>}
          </p>
        </div>
        {isDistCoord && !isReadOnly && (
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={openAdd}>
            {t('addBranch')}
          </motion.button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, background: 'var(--sindhi-cream)', padding: 6, borderRadius: 16, width: 'fit-content', direction: isRTL ? 'rtl' : 'ltr' }}>
        {[
          ['active', t('active')],
          ['unactive', t('unactive')]
        ].map(([v, l]) => (
          <button key={v} className={`btn-tab ${tab === v ? 'active' : ''}`} style={{ padding: '10px 24px', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: tab === v ? 'white' : 'transparent', color: tab === v ? 'var(--sindhi-navy)' : 'var(--sindhi-text-muted)', boxShadow: tab === v ? '0 4px 12px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setTab(v); setPage(1); }}>
            {l}
            {v === 'unactive' && unactiveBranches.length > 0 && <span style={{ background: 'var(--sindhi-maroon)', color: 'white', borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>{unactiveBranches.length}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('branchName')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('districts')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('City/Village')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('status')}</th>
                  {!isReadOnly && <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {list.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 80, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('noBranchesFound')}
                      </td>
                    </motion.tr>
                  ) : (
                    list.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((b) => {
                      const dist = districts.find(d => d._id === b.districtId);
                      const secretary = approvedMembers.find(m => m._id === b.secretaryId);
                      return <motion.tr key={b._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{renderBranchName(b)}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('secretary')}: {secretary ? (isRTL ? (secretary.full_name_sindhi || secretary.full_name) : secretary.full_name) : t('tbd')}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, color: 'var(--sindhi-text)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontWeight: 600 }}>{dist ? renderDistName(dist) : (b.districtId || '—')}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, color: 'var(--sindhi-text)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (b.cityVillageSindhi || b.cityVillage) : b.cityVillage}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: b.status === 'active' ? '#e1f9eb' : b.status === 'pending' ? '#fff5e6' : '#feecef', color: b.status === 'active' ? '#1da352' : b.status === 'pending' ? '#d97706' : '#e11d48', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                            {t(b.status)}
                          </span>
                        </td>
                        {!isReadOnly && (
                          <td style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                              {tab === 'pending' ? (
                                <>
                                  <button className="btn-action" style={{ background: '#1da352', color: 'white', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => approve(b._id, 'active')}>{t('approve')}</button>
                                  <button className="btn-action" style={{ background: '#e11d48', color: 'white', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => approve(b._id, 'inactive')}>{t('reject')}</button>
                                </>
                              ) : (
                                <>
                                  <button className="btn-action" style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', border: '1px solid var(--sindhi-cream)', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => openEdit(b)}>{t('edit')}</button>
                                  <button className="btn-action" style={{ background: 'var(--sindhi-gold)', color: 'white', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setSelectedBranch(b); setSecretaryModal(true); }}>{t('assignSecretary')}</button>
                                  <button className="btn-action" style={{ background: '#feecef', color: '#e11d48', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => deleteBranch(b._id)}>{t('delete')}</button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </motion.tr>;
                      ;
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24 }}>
            <Pagination page={page} totalPages={Math.ceil(list.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}

      {/* Modals */}
      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 700, maxHeight: '90vh', overflowY: 'auto', borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 24, fontWeight: 800 }}>{editId ? t('edit') : t('register')} {t('branch')}</h2>
                <button style={{ background: 'white', border: '1px solid var(--sindhi-cream)', width: 36, height: 36, borderRadius: 10, cursor: 'pointer' }} onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('branchNameEnglish')} *</label>
                    <input className="form-input" placeholder="e.g., Clifton Branch" value={form.name} onChange={e => set('name', e.target.value)} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label" >{t('branchNameSindhi')} *</label>
                    <input className="form-input" placeholder="مثال طور: ڪلفٽن برانچ" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.nameSindhi} onChange={e => set('nameSindhi', e.target.value)} required />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('districts')} *</label>
                    <select
                      className="form-input"
                      value={form.districtId}
                      onChange={e => set('districtId', e.target.value)}
                      required
                      disabled={isDistCoord}
                    >
                      <option value="">{t('selectDistrict')}</option>
                      {districts.map(d => <option key={d._id} value={d._id}>{renderDistName(d)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('established')}</label>
                    <input className="form-input" type="date" value={form.established || ''} onChange={e => set('established', e.target.value)} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('City/Village English')} *</label>
                    <input className="form-input" placeholder="e.g., Clifton" value={form.cityVillage} onChange={e => set('cityVillage', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" >{t('City/Village Sindhi')} *</label>
                    <input className="form-input" placeholder="مثال طور: ڪلفٽن" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.cityVillageSindhi} onChange={e => set('cityVillageSindhi', e.target.value)} required />
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('status')} *</label>
                    <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)} required>
                      <option value="active">{t('active')}</option>
                      <option value="unactive">{t('unactive')}</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t('Description in English')}</label>
                    <textarea className="form-input" rows={3} placeholder="Enter description in English" value={form.description || ''} onChange={e => set('description', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label" >{t('Description in Sindhi')}</label>
                    <textarea className="form-input" rows={3} placeholder="تفصيل سنڌي ۾ داخل ڪريو" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.descriptionSindhi || ''} onChange={e => set('descriptionSindhi', e.target.value)} />
                  </div>
                </div>
                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1, borderRadius: 12, fontWeight: 800 }}>{editId ? t('save') : t('submit')}</button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50, padding: '0 32px', borderRadius: 12, fontWeight: 800 }}>{t('cancel')}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {secretaryModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSecretaryModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 500, borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: 20, fontWeight: 800 }}>{t('assignSecretary')}</h2>
                <button onClick={() => setSecretaryModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
              <div style={{ padding: 32, maxHeight: '60vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {approvedMembers.length === 0 ? <p>{t('noApprovedMembers')}</p> : approvedMembers.map(m => (
                    <button key={m._id} onClick={() => assignSecretary(m)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderRadius: 12, border: '1px solid var(--sindhi-cream)', background: 'white', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? m.full_name_sindhi : m.full_name}</div>
                        <div style={{ fontSize: 12, opacity: 0.6 }}>{m.contact_info.email}</div>
                      </div>
                      <span style={{ fontSize: 20 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
